import { Component, OnInit, Input, Output, OnDestroy, EventEmitter, ViewChild, ElementRef, AfterViewInit, HostListener } from '@angular/core';
import { GridHeader, GridResponse, CustomButton, CustomRow, CustomButtonAction, GridMainFilter, GoupedColumn, GroupFunction, SelectionMode, SortedColumn } from './grid.model';
import { Observable, Subscription, Subject } from 'rxjs';
import { DateInputModel } from '../date-input/date-input.model';
import { MenuButton } from '../tabmenu/tabmenu.model';
import { TabMenuComponent } from '../tabmenu/tabmenu.component';
import { SpinnerService } from '../../services/spinner-handler/spinner-handler.service';
import { ContextMenuComponent } from 'ngx-contextmenu';
import { ColumnEditorComponent } from './column-editor/column-editor.component';
import { DialogService } from '../dialog/dialog.service';
import { DialogConfig } from '../dialog/dialog-config';
import { GridRest } from './grid.rest';

@Component({
    selector: 'app-grid',
    templateUrl: './grid.component.html',
    styleUrls: ['./grid.component.scss']
})
export class GridComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() headers: GridHeader[];
    @Input() customButtons: CustomButton[] = [];
    @Input() customRow: CustomRow;
    @Input() filtrable: boolean = false;
    @Input() sortable: boolean = false;
    @Input() paged: boolean = false;
    @Input() pageSizes = [10, 25, 50, 150];
    @Input() pageSize: number = 10;
    @Input() allowAdding: boolean = false;
    @Input() editable: boolean = false;
    @Input() removable: boolean = false;
    @Input() dataRestPath: string;
    @Input() localData: any[];
    @Input() onlocalDataLoaded: EventEmitter<any>;
    @Input() showGroupingRow: boolean = false;
    @Input() columnsToGroup: GoupedColumn[] = [];
    @Input() showMenu: boolean = false;
    @Input() menuButtons: MenuButton[];
    @Input() selectionMode: SelectionMode = SelectionMode.none;
    @Input() initialFilters: any;
    @Input() lockInitialFilters: boolean = false;
    @Input() initialPageNumber: number = 0;
    @Input() initialPageSize: number;
    @Input() initialSortedColumn: SortedColumn;
    @Input() isWithMainFilter: boolean = false;
    @Input() withoutSpinner: boolean = false;
    @Input() selectedItemId: any;
    @Input() isSelectedItemUser: boolean = false;
    @Input() withRestoring = true;
    @Input() isModal = false;
    @Input() projectTypeName: string;
    @Input() editableColumns: boolean = true;

    @Output() onRowEdit = new EventEmitter<any>();
    @Output() onRowDelete = new EventEmitter<any>();
    @Output() onRowAdd = new EventEmitter<any>();
    @Output() onCustomButtonAction = new EventEmitter<CustomButtonAction>();
    @Output() onRowFocus = new EventEmitter<any>();
    @Output() onFiltersRestore = new EventEmitter<any>();
    @Output() onAfterViewInit = new EventEmitter<any>();
    @Output() onSelectedItemsChange = new EventEmitter<any>();

    public rows: any[];
    public currentPage = 0;
    public dataSize = 1000;
    public previousPages: number[] = [];
    public currentPageNumber = 1;
    public nextPages: number[] = [];
    public pageCount = 10;
    public dateFilterValues: any = {};
    public mainFilter: GridMainFilter[];
    public groupedValues: any = {};
    public itemsCheck: boolean[] = [];
    public selectAll: boolean = false;
    public isAnyRowSelected: boolean = false;
    public filters: any = {};
    public sortedColumn: string;
    public isAsc: boolean;
    public focusedRowId: number;
    public isCtrlKeyPressed: boolean = false;
    public visibleHeaders: GridHeader[];
    public isBodyContextMenuEnabled: boolean = false;

    private isSortedNumber: boolean;
    private changeGridStateSource = new Subject();
    private changeGridState$ = this.changeGridStateSource.asObservable();
    private changeSubscription: Subscription;
    private changeDebounceTime: number = 300;
    private localDataLoadSubscription: Subscription;
    private dateFormat: string = 'dd.MM.yyyy';
    private columnSettings: any = {};
    private columnsSettingsJson: string;

    private headerTypesClasses: any = {
        bool: 'center middle-align text-center'
    };

    private get selectionModes() { return SelectionMode; }

    @ViewChild(TabMenuComponent) gridMenu: TabMenuComponent;
    @ViewChild('headerContextMenu') headerContextMenu: ContextMenuComponent;
    @ViewChild('bodyContextMenu') bodyContextMenu: ContextMenuComponent;

    constructor(
        private gridRest: GridRest,
        private spinnerService: SpinnerService,
        public dialog: DialogService
    ) {
        this.setLocalData = this.setLocalData.bind(this);
        this.configureForLocalProcessing = this.configureForLocalProcessing.bind(this);
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if (event.which == 17) {
            this.isCtrlKeyPressed = true;
        }
    }

    @HostListener('document:keyup', ['$event'])
    handleKeyboardEvent2(event: KeyboardEvent) {
        if (event.which == 17) {
            this.isCtrlKeyPressed = false;
        }
    }

    public ngOnInit() {
        this.visibleHeaders = this.headers;
        if (this.onlocalDataLoaded != null) {
            this.localDataLoadSubscription = this.onlocalDataLoaded
                .subscribe(data => {
                    this.setLocalData(data);
                    //this.localData = data;
                    this.configureForLocalProcessing();
                });
        }

        if (this.initialPageNumber > 0) {
            this.currentPage = this.initialPageNumber;
            this.currentPageNumber = this.currentPage + 1;
        }

        if (this.initialPageSize) {
            this.pageSize = this.initialPageSize;
        }

        this.headers.forEach(header => {
            if (header.propertyType === 'date') {
                this.dateFilterValues[header.propertyName] = new DateInputModel();
                if (this.initialFilters && this.initialFilters[header.propertyName]) {
                    if (!(this.initialFilters[header.propertyName] instanceof Date)) {
                        this.initialFilters[header.propertyName] = new Date(this.initialFilters[header.propertyName]);
                    }
                    this.dateFilterValues[header.propertyName].value = this.initialFilters[header.propertyName];
                    this.dateFilterValues[header.propertyName].isReadOnly = this.lockInitialFilters;
                } else {
                    this.dateFilterValues[header.propertyName].value = null;
                }
                this.filters[header.propertyName] = this.dateFilterValues[header.propertyName].value;
            } else {
                if (this.initialFilters && this.initialFilters[header.propertyName]) {
                    this.filters[header.propertyName] = this.initialFilters[header.propertyName];
                } else {
                    this.filters[header.propertyName] = '';
                }
            }
        });


        if (this.initialSortedColumn) {
            this.sortedColumn = this.initialSortedColumn.columnName;
            this.isAsc = this.initialSortedColumn.isAscending;
        }
        else {
            let sortedColumn = this.headers.filter(i => i.isInitSort == true)[0];
            if (sortedColumn != null) {
                this.sortedColumn = sortedColumn.propertyName;
                this.isAsc = sortedColumn.isInitSortAsc;

                if (sortedColumn.propertyType == 'number')
                    this.isSortedNumber = true;
                else this.isSortedNumber = false;
            }
            else {
                this.sortedColumn = this.headers[0].propertyName;
                this.isAsc = true;

                if (this.headers[0].propertyType == 'number')
                    this.isSortedNumber = true;
                else this.isSortedNumber = false;
            }
        }

        if (this.onlocalDataLoaded == null)
            this.changeGridStateSource.next({});

    }

    ngAfterViewInit() {
        this.onAfterViewInit.emit();
        this.changeSubscription = this.changeGridState$
            .debounceTime(this.changeDebounceTime)
            .switchMap(() =>
                this.loadData().catch((e: any) => Observable.of(null)))
            .subscribe((data: GridResponse) => {
                this.processData(data);
                if (!this.withoutSpinner && this.localData == null) {
                    this.spinnerService.hide();
                }
            },
                (error) => {
                    if (!this.withoutSpinner && this.localData == null) {
                        this.spinnerService.hide();
                    }
                });
        this.changeGridStateSource.next({});
    }

    errorHandler(error: any): void {
        console.log(error);
    }

    public ngOnDestroy() {
        this.changeSubscription.unsubscribe();
    }

    public refreshData(resetPage: boolean = true) {
        if (resetPage) {
            this.currentPage = 0;
        }
        this.changeGridStateSource.next({});
    }

    public getLastVisibleElement() {
        return ((this.currentPage * this.pageSize) + +this.pageSize);
    }

    changePreviousPage(newPage: number) {
        if (this.currentPage === 0) {
            return;
        } else {
            this.changePage(newPage);
        }
    }

    public setRestPath(dataRestPath: string) {
        this.dataRestPath = dataRestPath;
    }

    public changeNextPage(newPage: number) {
        if (this.currentPage >= this.pageCount - 1) {
            return;
        } else if (newPage >= this.pageCount) {
            this.changePage(newPage - 1);
        } else {
            this.changePage(newPage);
        }
    }

    private onFiltersChange() {
        this.currentPage = 0;
        this.focusedRowId = undefined;
        this.changeGridStateSource.next({});
    }

    private sort(header: GridHeader) {
        if (!this.sortable) {
            return;
        }

        if (header.propertyName === this.sortedColumn) {
            this.isAsc = !this.isAsc;
        } else {
            this.sortedColumn = header.propertyName;
            this.isAsc = true;

            if (header.propertyType === 'number') {
                this.isSortedNumber = true;
            } else { this.isSortedNumber = false; }
        }
        //    this.withRestoring = false;
        this.currentPage = 0;
        this.focusedRowId = undefined;
        this.changeGridStateSource.next({});
    }

    private resetFilters() {
        this.headers.forEach(header => {
            if (header.propertyType === 'date') {
                this.dateFilterValues[header.propertyName].value = null;
                this.filters[header.propertyName] = this.dateFilterValues[header.propertyName].value;
            } else {
                this.filters[header.propertyName] = '';
            }
        });
        this.currentPage = 0;
        this.changeGridStateSource.next({});
    }

    public refreshAndLoadData() {
        const request = {
            filter: {},
            orderBy: this.sortedColumn,
            isAscending: this.isAsc,
            pageNumber: this.currentPage,
            pageSize: 1000,
            mainFilter: this.mainFilter,
            withRestoring: this.withRestoring,
            isModal: this.isModal
        };

        if (this.dataRestPath) {
            return this.gridRest.getGridData(this.dataRestPath, request)
                .catch((e: any) => Observable.of(null))
                .subscribe(
                    data => {
                        this.setLocalData(data.elements);
                        //this.localData = data;
                        this.processLocalData();
                        this.processData(data);
                        this.configureForLocalProcessing();
                    },
                    (error) => {
                        if (!this.withoutSpinner) {
                            this.spinnerService.hide();
                        }
                    });
        }
    }

    private loadData() {
        if (this.localData != null) {
            return this.processLocalData();
        }
        if (this.localDataLoadSubscription != null) {
            return;
        }

        if (!this.withoutSpinner) {
            this.spinnerService.show();
        }

        if (this.isWithMainFilter && !this.mainFilter) {
            return Observable.of(null);
        }

        let request = {
            filter: this.filters,
            orderBy: this.sortedColumn,
            isAscending: this.isAsc,
            pageNumber: this.currentPage,
            pageSize: this.pageSize,
            mainFilter: this.mainFilter,
            withRestoring: this.withRestoring,
            isModal: this.isModal
        };

        if (this.selectedItemId) {
            let propertyName = this.isSelectedItemUser ? 'userId' : 'itemId';
            request[propertyName] = this.selectedItemId;
        }

        if (this.dataRestPath) {
            return this.gridRest.getGridData(this.dataRestPath, request);
        }
        return Observable.of({});
    }

    private processData(data: GridResponse) {
        if (!data) {
            this.dataSize = 0;
            return;
        }

        this.processColumnSettings(data.columnsSettings, data.orderBy);

        this.rows = data.elements;
        this.dataSize = data.count;
        this.currentPage = data.pageNumber;
        this.pageCount = Math.ceil(this.dataSize / this.pageSize);
        if (this.currentPage >= this.pageCount) {
            this.currentPage = 0;
            if (data.count > 0) {
                this.refreshData();
            }
            this.mainFilter = data.restoredFilters;
            this.sortedColumn = data.orderBy;
            this.isAsc = data.isAscending;
            this.onFiltersRestore.emit(this.mainFilter);
            this.withRestoring = false;
            return;
        }

        this.fillPaginationArrays();

        if (this.showGroupingRow && this.rows && this.rows.length > 0) {
            this.processColumnGrouping();
        }

        if (this.selectionMode === SelectionMode.multi && this.rows) {
            this.prepareRowsForChecking();
        }

        if (this.selectedItemId) {
            setTimeout(() => {
                this.setSelectedRowId();
                this.scrollToSelectedRow();
            }, 200);
        }
        if (data.elements) {
            if (this.withRestoring) {
                this.sortedColumn = data.orderBy;
                this.mainFilter = data.restoredFilters;
                this.isAsc = data.isAscending;
                this.onFiltersRestore.emit(this.mainFilter);
            }
            this.withRestoring = false;
        }
    }

    private processLocalData() {
        var processedData = this.filterLocalData();
        return Observable.of(processedData);
    }

    private filterLocalData() {
        var dataToProcess = this.localData;
        var processResult = { elements: [], count: 0, pageNumber: 0, orderBy: this.sortedColumn, isAscending: this.isAsc };
        ``
        if (this.filtrable) {
            let propertiesToFilter = Object.getOwnPropertyNames(this.filters);
            if (dataToProcess.length === 0)
                return processResult;

            propertiesToFilter.forEach(prop => {
                dataToProcess.forEach(row => {
                    if (row[prop] == null) row[prop] = '';
                });
                if (this.filters[prop] !== null) {
                    if (dataToProcess[0] != undefined && dataToProcess[0][prop] != 'undefined' && (typeof dataToProcess[0][prop] === "string" || typeof dataToProcess[0][prop] === "object")) {
                        let header = this.headers.find(h => h.propertyName === prop);
                        if (header && header.propertyType === 'date') {
                            dataToProcess = dataToProcess.filter(row => new Date(row[prop]).toDateString() === (this.filters[prop] as Date).toDateString());
                        }
                        else
                            dataToProcess = dataToProcess.filter(row => row[prop].toLowerCase().includes(this.filters[prop].toLowerCase()));
                    }
                    else if (dataToProcess[0] != undefined && dataToProcess[0][prop] != 'undefined' && typeof dataToProcess[0][prop] === "number")
                        dataToProcess = dataToProcess.filter(row => row[prop] >= this.filters[prop]);
                }
            });
        }

        let header = this.headers.find(h => h.propertyName === this.sortedColumn);
        if (this.isSortedNumber) {
            dataToProcess = this.isAsc ? dataToProcess.sort((a: number, b: number) => a[this.sortedColumn] - b[this.sortedColumn])
                : dataToProcess.sort((a: number, b: number) => b[this.sortedColumn] - a[this.sortedColumn]);
        } else if (header && header.propertyType === 'date') {
            dataToProcess = this.isAsc ? dataToProcess.sort((a: Date, b: Date) => new Date(a[this.sortedColumn]).valueOf() < new Date(b[this.sortedColumn]).valueOf() ? -1 : 1)
                : dataToProcess.sort((a: Date, b: Date) => new Date(a[this.sortedColumn]).valueOf() >= new Date(b[this.sortedColumn]).valueOf() ? -1 : 1);
        } else {
            dataToProcess = this.isAsc ? dataToProcess.sort((a: any, b: any) => a[this.sortedColumn] < b[this.sortedColumn] ? -1 : 1)
                : dataToProcess.sort((a: any, b: any) => a[this.sortedColumn] >= b[this.sortedColumn] ? -1 : 1);
        }


        if (this.paged) {
            let selectedIndex = dataToProcess.findIndex(x => x.id === this.selectedItemId);
            if (selectedIndex !== -1 && dataToProcess.length > this.pageSize) {
                this.currentPage = Math.floor(selectedIndex / this.pageSize);
            }

            this.pageCount = Math.ceil(this.dataSize / this.pageSize);
            if (this.currentPage >= this.pageCount && this.pageCount > 0) {
                this.currentPage = 0;
                if (dataToProcess.length > 0)
                    this.refreshData();
                return;
            }
        }

        processResult.count = dataToProcess.length;
        processResult.pageNumber = this.currentPage;
        let firstRowIndex: number = this.currentPage * this.pageSize;
        dataToProcess = dataToProcess.slice(firstRowIndex, firstRowIndex + this.pageSize);
        processResult.elements = dataToProcess;


        if (this.selectedItemId) {
            setTimeout(() => {
                this.setSelectedRowId();
                this.scrollToSelectedRow();
            }, 200);
        }
        return processResult;
    }

    private fillPaginationArrays() {
        const minPage = this.getMinPage();
        const maxPage = this.getMaxPage();

        this.previousPages = [];
        for (let i = minPage; i < this.currentPage; i++) {
            this.previousPages.push(i);
        }

        this.currentPageNumber = this.currentPage + 1;
        this.nextPages = [];

        for (let i = this.currentPage + 1; i < maxPage; i++) {
            this.nextPages.push(i);
        }
    }

    private changePage(newPage: number) {
        this.currentPage = newPage;
        this.focusedRowId = undefined;
        this.changeGridStateSource.next({});
    }

    private customTrackBy(index: number, obj: any): any {
        return index;
    }

    private remove(row: any) {
        if (this.removable) {
            this.onRowDelete.emit(row);
        }
    }

    private edit(row: any) {
        if (this.editable) {
            this.onRowEdit.emit(row);
        }
    }

    private add(event: any) {
        if (event.keyCode === 13) { //block enter keypress
            event.preventDefault();
            return;
        }
        if (this.allowAdding) {
            this.onRowAdd.emit();
        }
    }

    private customButtonAction(actionName: string, row: any) {
        this.onCustomButtonAction.emit({
            actionName: actionName,
            selectedRow: row
        });
    }

    private changePageSize(newPageSize: number) {
        this.pageSize = newPageSize;
        this.currentPage = 0;
        this.changeGridStateSource.next({});
    }

    private getMinPage() {
        return Math.max(this.currentPage - 3, 0);
    }

    private getMaxPage() {
        return Math.min(this.currentPage + 4, this.pageCount);
    }

    //   private getRowClasses(row) {
    //       if (this.customRow) {
    //           return this.customRow.getRowClasses(row);
    //       } else {
    //           return '';
    //       }
    //   }

    private configureForLocalProcessing() {
        this.changeDebounceTime = 0;
        this.changeGridStateSource.next();
    }

    private setLocalData(data) {
        this.localData = data;
    }

    public onDateFilterChange(propertyName: string) {
        if (this.dateFilterValues[propertyName].value != null) {
            this.dateFilterValues[propertyName].value.setUTCHours(0, 0, 0, 0);
        }
        this.filters[propertyName] = this.dateFilterValues[propertyName].value;
        this.onFiltersChange();
    }

    private processColumnGrouping() {
        this.columnsToGroup.forEach(groupedColumn => {
            switch (groupedColumn.func) {
                case GroupFunction.sum:
                    var header = this.headers.find(h => h.propertyName === groupedColumn.name);
                    var isNumber = header.propertyType === 'number';
                    var sum = this.getSumOfColumnValues(groupedColumn.name, isNumber);
                    this.groupedValues[groupedColumn.name] = { func: 'SUM', value: sum };
                    break;

                case GroupFunction.avg:
                    var header = this.headers.find(h => h.propertyName === groupedColumn.name);
                    var isNumber = header.propertyType === 'number';
                    var sum = this.getSumOfColumnValues(groupedColumn.name, isNumber);
                    var avg = sum / this.rows.length;
                    this.groupedValues[groupedColumn.name] = { func: 'AVG', value: avg };
                    break;

                case GroupFunction.min:
                    var header = this.headers.find(h => h.propertyName === groupedColumn.name);
                    if (header.propertyType === 'number') {
                        var values = this.rows.map(row => row[groupedColumn.name]);
                        var min = Math.min(...values);
                        this.groupedValues[groupedColumn.name] = { func: 'MIN', value: min };
                    } else {
                        var minString = this.sortRowsByColumn(groupedColumn.name, true)[0][groupedColumn.name];
                        this.groupedValues[groupedColumn.name] = { func: 'MIN', value: minString };
                    }
                    break;

                case GroupFunction.max:
                    var header = this.headers.find(h => h.propertyName === groupedColumn.name);
                    if (header.propertyType === 'number') {
                        var values = this.rows.map(row => row[groupedColumn.name]);
                        var max = Math.max(...values);
                        this.groupedValues[groupedColumn.name] = { func: 'MAX', value: max };
                    } else {
                        var maxString = this.sortRowsByColumn(groupedColumn.name, false)[0][groupedColumn.name];
                        this.groupedValues[groupedColumn.name] = { func: 'MAX', value: maxString };
                    }
                    break;
            }
        })
    }

    private getSumOfColumnValues(columnName: string, isNumber: boolean): number {
        var sum: number = 0;
        this.rows.forEach(row => {
            sum += isNumber ? (row[columnName] === "" ? 0 : row[columnName]) : +row[columnName];
        });

        return sum;
    }

    private sortRowsByColumn(columnName: string, asc: boolean): any[] {
        var sortedRows = asc
            ? this.rows.sort((a: any, b: any) => a[columnName] < b[columnName] ? -1 : 1)
            : this.rows.sort((a: any, b: any) => a[columnName] >= b[columnName] ? -1 : 1)
        return sortedRows;
    }

    private prepareRowsForChecking() {
        this.itemsCheck = [];
        this.selectAll = false;
        for (let index = 0; index < this.rows.length; index++) {
            const row = this.rows[index];
            row['checkRowId'] = index;
            this.itemsCheck[index] = false;
        }
        this.updateMenuButtonsState(false);
    }

    public updateAllSelections() {
        for (let i = 0; i < this.itemsCheck.length; i++) {
            this.itemsCheck[i] = this.selectAll;
        }
        this.updateMenuButtonsState(true);
    }

    public getSelectedItems(): any[] {
        if (!(this.selectionMode === SelectionMode.multi)) {
            return [];
        }

        var items = this.rows.filter(row => this.itemsCheck[row['checkRowId']]);
        return items;
    }

    public updateMenuButtonsState(fromSelectAll: boolean) {
        if (!this.menuButtons || !this.gridMenu) {
            return;
        }

        if (fromSelectAll === true) {
            this.isAnyRowSelected = this.selectAll;
        } else {
            this.isAnyRowSelected = this.itemsCheck.some(value => value === true);
        }

        this.isBodyContextMenuEnabled = this.menuButtons && this.menuButtons != null && this.menuButtons.some(butt =>
            (butt.enabled && !butt.isForSelected) || (butt.enabled && butt.isForSelected && this.isAnyRowSelected));
    }

    public checkSelection() {
        let selectedItems = this.getSelectedItems();
        this.onSelectedItemsChange.emit(selectedItems);
    }

    public focusRow(row: any, rowIndex: number) {
        if (this.selectionMode === SelectionMode.multi && this.isCtrlKeyPressed) {
            this.itemsCheck[row['checkRowId']] = !this.itemsCheck[row['checkRowId']];
            this.updateMenuButtonsState(false);
        }
        this.focusedRowId = rowIndex;
        this.onRowFocus.emit(row);
    }

    private setSelectedRowId() {
        if (!this.rows) {
            return;
        }
        if (this.selectedItemId) {
            for (let index = 0; index < this.rows.length; index++) {
                const row = this.rows[index];
                if (row.id === this.selectedItemId) {
                    this.focusedRowId = index;
                    break;
                }
            }
            this.selectedItemId = undefined;
        }
    }

    private scrollToSelectedRow() {
        if (!this.rows) {
            return;
        }
        let timeoutValue = this.rows.length * 25; //ms
        setTimeout(() => {
            let selectedRowRef = document.getElementById('selectedRow');
            if (selectedRowRef) {
                const elementRect = selectedRowRef.getBoundingClientRect();
                const absoluteElementTop = elementRect.top + window.pageYOffset;
                const middle = absoluteElementTop - (window.innerHeight / 2);
                window.scrollTo(0, middle);
            }
        }, timeoutValue);
    }

    public openColumnEditor() {
        let settingsCount = Object.keys(this.columnSettings).length;
        let tempItems = new Array(settingsCount);
        let endIndex = 0;

        this.headers.forEach(item => {
            let settings = this.columnSettings[item.propertyName];
            if (settings) {
                item.isVisible = settings.isVisible !== false;
                tempItems.splice(settings.index, 1, item);
            } else {
                item.isVisible = true;
                tempItems.splice(settingsCount + endIndex++, 0, item);
            }
        });

        tempItems = tempItems.filter(item => item);

        let dialogConf = new DialogConfig();
        dialogConf.selectionMode = SelectionMode.none;
        dialogConf.title = 'Edycja kolumn';
        dialogConf.maxwidth = '350px';
        dialogConf.data = { items: tempItems };

        const dialogRef = this.dialog.open(ColumnEditorComponent, dialogConf);
        dialogRef.afterClosed.subscribe(data => {
            if (data) {
                this.visibleHeaders = data.visibleItems;
                this.columnSettings = data.columnSettings;
                let settingsJson = JSON.stringify(this.columnSettings);
                this.columnsSettingsJson = settingsJson;
                if (this.dataRestPath) {
                    let gridPath = this.dataRestPath;
                    if (this.projectTypeName) {
                        gridPath += this.projectTypeName;
                    }
                    this.gridRest.saveColumnsSettings(gridPath, settingsJson).subscribe();
                }

                let isFiltersOrOrderingChanged = false;

                this.headers.forEach(header => {
                    if (!this.visibleHeaders.some(item => item.propertyName === header.propertyName)) {
                        if (header.propertyType === 'date') {
                            if (this.filters[header.propertyName] !== null) {
                                this.dateFilterValues[header.propertyName].value = null;
                                this.filters[header.propertyName] = this.dateFilterValues[header.propertyName].value;
                                isFiltersOrOrderingChanged = true;
                            }
                        } else {
                            if (this.filters[header.propertyName] !== '') {
                                this.filters[header.propertyName] = '';
                                isFiltersOrOrderingChanged = true;
                            }
                        }
                    }
                });

                if (!this.visibleHeaders.some(item => item.propertyName === this.sortedColumn)) {
                    this.sortedColumn = this.visibleHeaders[0].propertyName;
                    isFiltersOrOrderingChanged = true;
                }

                if (isFiltersOrOrderingChanged == true) {
                    this.refreshData();
                }
            }
        });
    }

    private processColumnSettings(settingsJson: string, orderByFromResponse: string) {
        if (settingsJson == null || settingsJson === this.columnsSettingsJson) {
            return;
        }
        let tempSettings = JSON.parse(settingsJson);
        if (tempSettings == null || tempSettings == {}) {
            return;
        }

        this.columnSettings = tempSettings;
        let settingsCount = Object.keys(this.columnSettings).length;
        let tempItems = new Array(settingsCount);
        let endIndex = 0;

        this.headers.forEach(item => {
            let settings = this.columnSettings[item.propertyName];
            if (settings) {
                item.isVisible = settings.isVisible !== false;
                if (item.isVisible === true) {
                    tempItems.splice(settings.index, 1, item);
                }
            } else {
                item.isVisible = true;
                tempItems.splice(settingsCount + endIndex++, 0, item);
            }
        });
        tempItems = tempItems.filter(item => item);

        let isFiltersOrOrderingChanged = false;

        this.headers.forEach(header => {
            if (!tempItems.some(item => item.propertyName === header.propertyName)) {
                if (header.propertyType === 'date') {
                    if (this.filters[header.propertyName] !== null) {
                        this.dateFilterValues[header.propertyName].value = null;
                        this.filters[header.propertyName] = this.dateFilterValues[header.propertyName].value;
                        isFiltersOrOrderingChanged = true;
                    }
                } else {
                    if (this.filters[header.propertyName] !== '') {
                        this.filters[header.propertyName] = '';
                        isFiltersOrOrderingChanged = true;
                    }
                }
            }
        });

        if (!tempItems.some(item => item.propertyName === orderByFromResponse)) {
            this.sortedColumn = tempItems[0].propertyName;
            isFiltersOrOrderingChanged = true;
        }
        this.visibleHeaders = tempItems;
        if (isFiltersOrOrderingChanged == true) {
            this.refreshData();
        }
    }
}
